import { Button } from "@/components/Base/Button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/Base/Dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/Base/Popover";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { capitalize, firestoreToDateTime } from "@/lib/utils";
import {
  ActionFieldLabels,
  AutomationHistoryChange,
  AutomationHistoryEntry,
  Card,
  Container,
  Shop,
} from "@/types/v2";
import { CopyIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { card } from "@/repositories/cards";
import { automationHistoryRepository } from "@/repositories/automationHistory";

interface DebugInfoProps {
  cardData: Card;
  containers?: Container[];
  setOpen: (open: boolean) => void;
  open: boolean;
  shop?: Shop;
  firestoreId?: string;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
const CardField = ({ name, value }: { name: string; value: any }) => {
  const [copiedText, copy] = useCopyToClipboard();
  const [copyPopoverOpen, setCopyPopoverOpen] = useState(false);
  const [copyResults, setCopyResults] = useState({ success: false, error: "" });

  const handleCopy = (text: string) => {
    copy(text)
      .then(() => {
        setCopyResults({ success: true, error: "" });
      })
      .catch((error) => {
        setCopyResults({ success: false, error: error });
        console.error(error);
      })
      .finally(() => {
        setTimeout(() => {
          setCopyPopoverOpen(false);
        }, 2000);
      });
  };
  return (
    <div className="flex flex-row items-center justify-start gap-4">
      <div className="font-bold">{name}:</div>
      <pre className={"text-wrap"}>{value ?? "N/A"}</pre>
      <div className="flex-grow" />
      <Popover open={copyPopoverOpen} onOpenChange={setCopyPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="smIcon"
            onClick={() => handleCopy(value)}
          >
            <CopyIcon size={18} />
          </Button>
        </PopoverTrigger>
        <PopoverContent>
          {copyResults.success
            ? `${copiedText} copied!`
            : `Error copying: ${copyResults.error}`}
        </PopoverContent>
      </Popover>
    </div>
  );
};
/**
 * Object/array values render as indented, multi-line JSON — the flat,
 * unspaced output of a bare JSON.stringify is unreadable for the nested
 * shapes (e.g. Timer state) most automation actions actually apply.
 * Scalars stay inline; indenting a bare string/number/boolean/null adds
 * nothing.
 */
const formatHistoryChange = (change: AutomationHistoryChange): string => {
  const label = change.actionField
    ? (ActionFieldLabels[change.actionField] ?? change.actionField)
    : "(reset)";
  const prefix = `${label} → ${change.actionEventType}:`;
  const isObjectValue =
    change.appliedValue !== null && typeof change.appliedValue === "object";
  return isObjectValue
    ? `${prefix}\n${JSON.stringify(change.appliedValue, null, 2)}`
    : `${prefix} ${JSON.stringify(change.appliedValue)}`;
};

export const DebugInfo = ({
  cardData,
  containers,
  open,
  setOpen,
  shop,
  firestoreId,
}: DebugInfoProps) => {
  const desiredContainers = useMemo(
    () => card.getContainerOptions(cardData, shop?.containers ?? []),
    [shop, cardData]
  );

  const [historyEntries, setHistoryEntries] = useState<
    AutomationHistoryEntry[]
  >([]);

  useEffect(() => {
    if (!open || !shop?.id || !cardData.id) {
      setHistoryEntries([]);
      return;
    }
    return automationHistoryRepository.subscribeByCard(
      shop.id,
      cardData.id,
      setHistoryEntries
    );
  }, [open, shop?.id, cardData.id]);

  const getContainerDisplayName = (containerId: string) => {
    if (!containerId) return "Unknown";
    const container = containers?.find((c) => c.id === containerId);
    return container?.displayName
      ? `${container.displayName} (${containerId})`
      : containerId;
  };

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Card Debug Info</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <CardField name="Firestore Shop ID" value={firestoreId} />
          <CardField name="Shop ID" value={shop?.shopId} />
          {shop?.tenantId && (
            <CardField name="Tenant ID" value={shop?.tenantId} />
          )}
          <CardField name="Card ID" value={cardData.id} />
          <CardField name="Repair Order Number" value={cardData.cardNumber} />
          <CardField
            name="Created Via"
            value={capitalize(cardData.source ?? "Unknown")}
          />
          <CardField name="Technician ID" value={cardData.technicianId} />
          <CardField name="Label" value={cardData.label} />
        </div>
        <h2 className={"text-lg font-bold text-primary"}>Card Sync Options</h2>
        <CardField
          name="Current Container"
          value={
            containers
              ? `${containers.find((c) => c.id === cardData.currentContainer)?.displayName ?? cardData.currentContainer} (${cardData.currentContainer})`
              : cardData.currentContainer
          }
        />
        <CardField
          name="Sync Tech Only"
          value={desiredContainers.syncTechOnly
            .map((t, i) => `${i + 1}. ${getContainerDisplayName(t)}`)
            .join(",\n ")}
        />
        <CardField
          name="Sync Label Only"
          value={desiredContainers.syncLabelOnly
            .map((t, i) => `${i + 1}. ${getContainerDisplayName(t)}`)
            .join(",\n ")}
        />
        <CardField
          name="Sync Tech & Label"
          value={desiredContainers.syncBoth
            .map((t, i) => `${i + 1}. ${getContainerDisplayName(t)}`)
            .join(",\n ")}
        />

        <h2 className={"text-lg font-bold text-primary"}>Card Timer</h2>
        <CardField
          name="Start Time"
          value={firestoreToDateTime(
            cardData.timer?.timeStarted
          )?.toLocaleString()}
        />
        <CardField
          name="Accumulated Time"
          value={cardData.timer?.accumulatedTime}
        />

        <h2 className={"text-lg font-bold text-primary"}>Automation History</h2>
        {historyEntries.length === 0 ? (
          <p>No automation history yet.</p>
        ) : (
          <div className="flex min-w-0 flex-col gap-3">
            {historyEntries.map((entry, i) => (
              <div key={i} className="flex flex-col gap-1">
                <div className="font-bold">
                  <span>{entry.automationName ?? entry.automationId}</span>
                  {" — "}
                  <span>
                    {firestoreToDateTime(entry.firedAt)?.toLocaleString()}
                  </span>
                </div>
                {entry.changes.map((change, j) => (
                  <pre
                    key={j}
                    className="min-w-0 whitespace-pre-wrap break-words pl-4 text-sm"
                  >
                    {formatHistoryChange(change)}
                  </pre>
                ))}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
